import { useState } from "react";
import { Check, ChevronRight, Copy } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { StatusDot } from "@shared/components/blocks";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@shared/components/ui/collapsible";
import type { MarketingItemView } from "@shared/lib/marketing-shared";
import { cn } from "@shared/lib/utils";

function CopyButton({ text, label = "Copier le texte" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }
  return (
    <Button onClick={copy} size="sm" type="button" variant="outline">
      {copied ? <Check /> : <Copy />} {copied ? "Copié" : label}
    </Button>
  );
}

/** Texte du post LinkedIn (replié) et autres textes à copier, sauts de ligne gardés. */
export function PostText({ post, texts }: Pick<MarketingItemView, "post" | "texts">) {
  const [open, setOpen] = useState(false);
  if (!post && texts.length === 0) return null;
  // Déclaré au catalogue mais introuvable sur le disque : on le dit, comme pour une variante absente.
  if (post?.missing && texts.length === 0) {
    return (
      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-0.5 border-t pt-3 text-[13px] text-muted-foreground">
        Texte du post LinkedIn <StatusDot tone="danger">Fichier absent</StatusDot>
      </div>
    );
  }

  return (
    <Collapsible className="mt-3 border-t pt-3" onOpenChange={setOpen} open={open}>
      <CollapsibleTrigger className="flex w-full cursor-pointer flex-wrap items-center gap-x-1.5 gap-y-0.5 text-left text-[13px] font-medium hover:text-foreground">
        <ChevronRight
          aria-hidden="true"
          className={cn("size-4 shrink-0 text-muted-foreground transition-transform duration-150", open && "rotate-90")}
        />
        {post ? "Texte du post LinkedIn" : "Textes à copier"}
        <span className="font-normal text-muted-foreground">
          · {post ? post.lengthLabel : texts.length}
          {post?.tooLong && <span className="text-destructive"> · trop long pour LinkedIn</span>}
        </span>
        {post?.missing && <StatusDot tone="danger">Fichier absent</StatusDot>}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-3 grid gap-4 pl-5.5 max-sm:pl-0">
          {post && !post.missing && (
            <div className="grid justify-items-start gap-2.5">
              <p className="m-0 max-w-[72ch] border-l-2 pl-3.5 text-[13px] leading-relaxed whitespace-pre-wrap">{post.text}</p>
              <CopyButton text={post.text} />
            </div>
          )}
          {texts.map((entry) => (
            <div className="grid justify-items-start gap-1.5" key={entry.label}>
              <span className="text-[12.5px] text-muted-foreground">{entry.label}</span>
              <p className="m-0 max-w-[72ch] border-l-2 pl-3.5 text-[13px] leading-relaxed whitespace-pre-wrap">{entry.text}</p>
              <CopyButton text={entry.text} />
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
